import { Link } from "react-router-dom";
import { ArrowRight, Award, PlayCircle, Calculator, Stethoscope, MapPin, Briefcase,
  CheckCircle2, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../api";
import { useAuth } from "../contexts/AuthContext";
import TipOfWeekSection from "../components/TipOfWeekSection";
import NewsletterCapture from "../components/NewsletterCapture";
import SocialProof from "../components/SocialProof";
import WallPreview from "../components/WallPreview";
import Seo from "../components/Seo";
import LazyVimeo from "../components/LazyVimeo";
import { trackEvent } from "../lib/analytics";
import { OFFER } from "../config/offer";

// Linda's hero - "The Original Landscaper" / "Garden Cottage Paradise" - one of her signature shots
const HERO_IMAGE = "/img/g/3trs5yij.webp";

const LANDING_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "Backyard Buddy",
  url: "https://backyardbuddy.app",
  applicationCategory: "DesignApplication",
  operatingSystem: "Web",
  description: "AI-powered landscape design and garden planning tools from 38-year landscape designer Linda Godlewsky Ugi.",
  offers: { "@type": "Offer", price: "4.99", priceCurrency: "USD" },
  creator: { "@type": "Person", name: "Linda Godlewsky Ugi" },
};

const FAQS = [
  {
    q: "Is Backyard Buddy really free to use?",
    a: `Yes! You get ${OFFER.FREE_DESIGNS_ON_SIGNUP} free AI design when you create your account — plus the Material Calculator, Buddy Care garden coach, Seasonal Planner, Plant Library, and all 60+ gallery photos free forever. No credit card needed. Premium (${OFFER.PRICE_LONG}) unlocks unlimited AI designs, Plant ER, and the local Marketplace, and you can earn extra free designs by inviting friends.`,
  },
  {
    q: "Do I need any design or gardening experience?",
    a: "Not a bit. Backyard Buddy was built for DIYers. If you can snap a photo and type a sentence, you can design your backyard. And when you get stuck, Buddy Care answers your questions with 38 years of Linda's landscaping know-how behind it.",
  },
  {
    q: "How does the AI backyard designer work?",
    a: "Upload a photo of your yard (or start from scratch), describe what you're dreaming of — like 'a cozy fire pit lounge with native plants' — and in about 20 seconds you get a photoreal redesign you can save, share, or hand to a contractor.",
  },
  {
    q: "Can I use Backyard Buddy on my phone?",
    a: "Absolutely. It works in any browser and installs like an app on iPhone or Android — no app store needed. Snap photos in the yard, design from the couch.",
  },
  {
    q: "Who's behind Backyard Buddy?",
    a: "That's Linda Godlewsky Ugi — 38 years of designing and building real landscapes. Every photo in the gallery is a yard she built with her own hands, and every tool in the app is something she wishes her DIY clients had years ago.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Landing() {
  const { user } = useAuth();
  const [previewItems, setPreviewItems] = useState([]);
  const [showSticky, setShowSticky] = useState(false);
  const designTo = user ? "/ai-designer" : "/register";
  const designLabel = user ? "Design my backyard" : OFFER.CTA;

  useEffect(() => {
    trackEvent("homepage_visit");
  }, []);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/gallery").then(({ data }) => {
      // Pick visually-strong photos for bento grid (some Linda favorites)
      const featured = data.items.filter(i =>
        ["Stunning Fire Pit Lounge", "Twilight Garden Paradise", "Cherry Blossom Deck",
         "Lakefront Perennial Paradise", "Cottage Garden BBQ Retreat", "Backyard Water Feature Garden"]
          .includes(i.title)
      );
      setPreviewItems(featured.length >= 6 ? featured.slice(0, 6) : data.items.slice(0, 6));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page" className={showSticky ? "pb-20 sm:pb-0" : ""}>
      <Seo
        title="Backyard Buddy — Free Yard Planner App & AI Backyard Designs"
        description="Free yard planner app: snap a photo of your yard, get an AI backyard design in minutes, then find plants and materials near you. Built on 38 years of landscape expertise from Linda."
        path="/"
        jsonLd={[LANDING_JSONLD, FAQ_JSONLD]}
      />
      {/* HERO */}
      <section className="relative w-full h-[88vh] min-h-[640px] overflow-hidden" data-testid="hero-section">
        <img
          src={HERO_IMAGE}
          alt="Linda's garden cottage paradise"
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 hero-gradient-side" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 h-full flex flex-col justify-end pb-16">
          {/* Linda's expertise badge */}
          <div className="inline-flex items-center gap-2 self-start px-4 py-2 rounded-full bg-[#D47C59] text-white mb-6 animate-fade-up stagger-1" data-testid="hero-expertise-badge">
            <Award className="w-4 h-4" strokeWidth={1.8} />
            <span className="overline">38+ Years of Landscape Expertise</span>
          </div>

          <h1 className="font-serif text-[#F9F6F0] text-5xl md:text-7xl lg:text-8xl font-light leading-[1.05] tracking-tight max-w-5xl animate-fade-up stagger-2" data-testid="hero-headline">
            Design your backyard
            <br />
            <span className="italic">before</span> you build it.
          </h1>
          <p className="text-[#F9F6F0]/90 text-lg md:text-xl font-light max-w-xl mt-6 leading-relaxed animate-fade-up stagger-3" data-testid="hero-subhead">
            Take a photo. See your possibilities. Find what you need locally.
          </p>
          <div className="flex flex-wrap gap-3 mt-8 animate-fade-up stagger-4">
            <Link to={designTo} onClick={() => { trackEvent("free_design_cta", { location: "hero" }); trackEvent("design_cta_click", { location: "hero" }); }} className="btn-accent" data-testid="hero-cta-design">
              {designLabel} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-medium text-sm border-2 border-[#F9F6F0] text-[#F9F6F0] bg-transparent hover:bg-[#F9F6F0] hover:text-[#1A2F25] transition-all duration-300"
              data-testid="hero-cta-gallery"
            >
              Browse Linda's 60+ designs
            </Link>
          </div>

          <p className="text-[#F9F6F0]/85 font-light text-sm mt-4 flex items-center gap-2 animate-fade-up stagger-4" data-testid="hero-trust-line">
            <CheckCircle2 className="w-4 h-4 text-[#D47C59]" strokeWidth={2} /> Free to start — no credit card needed
          </p>

          <p className="text-[#F9F6F0]/70 font-light text-sm mt-6 animate-fade-up stagger-5" data-testid="hero-credit">
            Built with care by <span className="font-medium text-[#D47C59]">Linda Godlewsky Ugi</span> · No stress. No guesswork. Just beautiful results.
          </p>
        </div>
      </section>

      {/* 3 STEPS — the customer journey with real visuals */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16" data-testid="three-steps-strip">
        <div className="text-center mb-10">
          <p className="overline text-[#D47C59] mb-3">How it works</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight">
            See your backyard <span className="italic">before</span> you build it.
          </h2>
          <p className="text-[#5C6B62] font-light mt-3">Three simple steps. One beautiful plan.</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            { n: "1", title: "Snap your space", body: "Take a picture of the area you want to transform. No measurements. No special tools. Just your phone.", img: "/img/g/linda-step1-snap.webp", alt: "Homeowner photographing backyard with smartphone for AI landscape design", label: "Your yard today" },
            { n: "2", title: "Describe your dream", body: "Tell Backyard Buddy what you love — plants, colors, privacy, patio ideas or landscape style. Plain words work.", img: "/img/g/linda-step2-describe.webp", alt: "Backyard photo uploaded into Backyard Buddy with a simple homeowner design request", label: "Just tell Buddy" },
            { n: "3", title: "See the possibility", body: "See a beautiful landscape concept based on your own space — before you start spending money.", img: "/img/g/linda-step3-possibility.webp", alt: "AI landscape design concept showing the same backyard transformed with perennials and privacy plants", label: "The same yard, after" },
          ].map((s, i) => (
            <div key={s.n} className={`bb-card overflow-hidden animate-fade-up stagger-${i + 1}`} data-testid={`three-step-${s.n}`}>
              <div className="relative h-48 md:h-56 overflow-hidden bg-[#E5E0D8]">
                <img src={s.img} alt={s.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 overline px-2.5 py-1 rounded-full bg-[#F9F6F0]/90 text-[#2D4A3E]">{s.label}</span>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-[#2D4A3E] text-[#F9F6F0] flex items-center justify-center font-serif flex-shrink-0">{s.n}</div>
                  <h3 className="font-serif text-2xl font-medium leading-tight">{s.title}</h3>
                </div>
                <p className="text-sm text-[#5C6B62] font-light leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-5 mt-10">
          <Link to={designTo} onClick={() => trackEvent("free_design_cta", { location: "three_steps" })} className="btn-accent" data-testid="three-steps-cta">
            {designLabel} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/how-it-works" className="text-sm text-[#5C6B62] hover:text-[#D47C59] transition-colors" data-testid="three-steps-tour-link">
            See the full tour →
          </Link>
        </div>
      </section>

      {/* SOCIAL PROOF — trust builds before the scroll */}
      <SocialProof />

      {/* WELCOME FROM LINDA — VIDEO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24" data-testid="welcome-video-section">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <p className="overline text-[#D47C59] mb-4">Welcome from Linda</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-tight mb-6">
              Meet the gardener
              <br />
              <span className="italic">behind the buddy.</span>
            </h2>
            <p className="text-[#5C6B62] font-light leading-relaxed mb-6">
              Hi, I'm Linda. For 38 years, my hands have been in the dirt — designing patios,
              planting hydrangeas, hauling boulders, and turning weekend warriors into proud
              backyard owners. Backyard Buddy is everything I've learned, packed into a tool
              that helps you do it too.
            </p>
            <p className="text-[#5C6B62] font-light leading-relaxed mb-8 italic">
              "No stress. No guesswork. Just beautiful results."
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/about" className="btn-outline" data-testid="welcome-about-cta">
                My story <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/gallery" className="btn-ghost flex items-center gap-2" data-testid="welcome-gallery-cta">
                See my work <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl overflow-hidden bb-card" data-testid="vimeo-embed">
              <LazyVimeo
                src="https://player.vimeo.com/video/1145359332?title=0&byline=0&portrait=0"
                title="Welcome from Linda — Backyard Buddy"
              />
            </div>
            <p className="text-xs text-[#5C6B62] font-light mt-3 flex items-center gap-2">
              <PlayCircle className="w-3.5 h-3.5" /> 1:18 · A quick hello from Linda
            </p>
          </div>
        </div>
      </section>

      {/* BENTO GALLERY PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline text-[#D47C59] mb-3">Linda's portfolio</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-tight">
              60+ real backyards.
              <br />
              <span className="italic">Every one a love letter.</span>
            </h2>
          </div>
          <Link to="/gallery" className="btn-outline hidden md:inline-flex" data-testid="gallery-cta">
            See them all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {previewItems.length >= 4 && (
          <div className="grid grid-cols-6 grid-rows-2 gap-4 h-[560px]" data-testid="bento-preview">
            <div className="col-span-3 row-span-2 rounded-2xl overflow-hidden relative group">
              <img src={previewItems[0]?.url} alt={previewItems[0]?.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-[#F9F6F0]">
                <p className="overline text-[#D47C59] mb-1">{previewItems[0]?.category}</p>
                <p className="font-serif text-2xl font-medium">{previewItems[0]?.title}</p>
              </div>
            </div>
            <div className="col-span-3 row-span-1 rounded-2xl overflow-hidden relative group">
              <img src="/img/g/linda-bluestone-path.webp" alt="Bluestone Patio Path" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
              <p className="absolute bottom-4 left-5 font-serif text-xl text-[#F9F6F0]">Bluestone Patio Path</p>
            </div>
            <div className="col-span-3 row-span-1 rounded-2xl overflow-hidden relative group">
              <img src={previewItems[3]?.url} alt={previewItems[3]?.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
              <p className="absolute bottom-4 left-5 font-serif text-lg text-[#F9F6F0]">{previewItems[3]?.title}</p>
            </div>
          </div>
        )}

        <div className="mt-8 md:hidden">
          <Link to="/gallery" className="btn-outline w-full justify-center">
            See them all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FEATURE CALLOUT */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 rounded-3xl overflow-hidden h-[420px] md:h-[520px] relative">
            <img
              src="/img/g/linda-perennial-border.webp"
              alt="Perennial border with Russian sage and pebble creek"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="md:col-span-5 md:pl-8">
            <p className="overline text-[#D47C59] mb-3">AI Designer · Powered by Gemini</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-tight mb-6">
              Tell us your dream.
              <br />
              <span className="italic">We'll grow it.</span>
            </h2>
            <p className="text-[#5C6B62] font-light leading-relaxed mb-6">
              Upload a photo of your bare backyard. Type a sentence. The AI returns a photoreal,
              landscape-architect-grade redesign — ready to share, save, or hand to your contractor.
              Just like having Linda right beside you.
            </p>
            <ul className="space-y-3 mb-8 text-sm font-light">
              <li className="flex items-start gap-3"><span className="text-[#D47C59] mt-1">✦</span> Your first design is on us — free with your account</li>
              <li className="flex items-start gap-3"><span className="text-[#D47C59] mt-1">✦</span> Edit your own photo or generate from scratch</li>
              <li className="flex items-start gap-3"><span className="text-[#D47C59] mt-1">✦</span> Unlimited designs on Premium ({OFFER.PRICE})</li>
            </ul>
            <Link to={designTo} onClick={() => { trackEvent("free_design_cta", { location: "feature_callout" }); trackEvent("design_cta_click", { location: "feature_callout" }); }} className="btn-primary" data-testid="ai-cta">
              {designLabel} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* LOVE YOUR DESIGN? NOW LET'S BUILD IT — tools in project order */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24" data-testid="build-it-section">
        <div className="text-center mb-12">
          <p className="overline text-[#D47C59] mb-3">Love your design?</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight">
            Now let&apos;s build it.
          </h2>
          <p className="text-[#5C6B62] font-light mt-3 max-w-xl mx-auto">
            Backyard Buddy walks you from dream to done — in the order a real project actually happens.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { to: "/material-calculator", icon: Calculator, title: "Material Calculator", body: "Know exactly how much mulch, stone, or soil you need.", tag: "Free" },
            { to: "/marketplace", icon: MapPin, title: "Local Marketplace", body: "Find plants, materials, nurseries and suppliers near you.", tag: "Premium" },
            { to: "/plant-er", icon: Stethoscope, title: "Plant ER", body: "Snap a struggling plant, get a diagnosis and treatment plan.", tag: "Premium" },
            { to: "/pros", icon: Briefcase, title: "Backyard Pros", body: "Get professional help when you need it, by zip code.", tag: "Free" },
          ].map((f) => (
            <Link to={f.to} key={f.to} className="bb-card p-5 relative group transition-all" data-testid={`build-it-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <span className={`overline absolute top-4 right-4 px-2 py-0.5 rounded-full ${
                f.tag === "Premium" ? "bg-[#D47C59] text-white" : "bg-[#E5E0D8] text-[#2D4A3E]"
              }`}>{f.tag}</span>
              <f.icon className="w-7 h-7 text-[#2D4A3E] mb-3 mt-2" strokeWidth={1.5} />
              <h3 className="font-serif text-xl font-medium mb-2">{f.title}</h3>
              <p className="text-[#5C6B62] font-light text-sm leading-relaxed mb-3">{f.body}</p>
              <span className="text-xs text-[#D47C59] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                Open <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
        </div>
        <p className="text-center text-sm text-[#5C6B62] font-light mt-8" data-testid="more-tools-line">
          Plus free tools for every gardener:{" "}
          <Link to="/buddy-care" className="underline underline-offset-4 hover:text-[#D47C59]">Buddy Care</Link>,{" "}
          <Link to="/seasonal-planner" className="underline underline-offset-4 hover:text-[#D47C59]">Seasonal Planner</Link>, and{" "}
          <Link to="/gallery" className="underline underline-offset-4 hover:text-[#D47C59]">Linda&apos;s Gallery</Link>.
        </p>
      </section>

      {/* SOCIAL PROOF STRIP moved up under hero */}

      <WallPreview />

      {/* TIP OF THE WEEK — middle of the page */}
      <TipOfWeekSection />

      {/* NEWSLETTER CAPTURE — high-conversion "give email, get PDF" moment */}
      <section className="max-w-4xl mx-auto px-6 lg:px-12 pb-24" data-testid="landing-newsletter-section">
        <NewsletterCapture source="landing_middle" variant="default" />
      </section>

      {/* FAQ — conversion objections + FAQPage SEO */}
      <section className="max-w-4xl mx-auto px-6 lg:px-12 pb-24" data-testid="faq-section">
        <p className="overline text-[#D47C59] mb-3 text-center">Good questions</p>
        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-center mb-10">
          Wondering something?
        </h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={f.q} className="bb-card px-6 py-4 group" data-testid={`faq-item-${i}`}>
              <summary className="font-serif text-lg md:text-xl cursor-pointer list-none flex items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
                {f.q}
                <ChevronDown className="w-4 h-4 text-[#D47C59] flex-shrink-0 transition-transform group-open:rotate-180" />
              </summary>
              <p className="text-[#5C6B62] font-light text-sm md:text-base leading-relaxed mt-3">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="rounded-3xl bg-[#2D4A3E] text-[#F9F6F0] p-12 md:p-20 relative overflow-hidden grain">
          <div className="relative z-10 max-w-2xl">
            <p className="overline text-[#D47C59] mb-4">From backyard dream to backyard project</p>
            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tight leading-tight mb-6">
              Your first design is on us.
            </h2>
            <p className="text-[#E5E0D8] font-light text-lg mb-8 max-w-lg leading-relaxed">
              {OFFER.PROMISE} Create your free account, see what your own backyard could
              become — then unlock unlimited designs, Plant ER, and the local Marketplace
              for {OFFER.PRICE}.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" onClick={() => { trackEvent("free_design_cta", { location: "landing_banner" }); trackEvent("registration_cta_click", { location: "landing_banner" }); }} className="btn-accent" data-testid="cta-register">{OFFER.CTA}</Link>
              <Link to="/pricing" className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-medium text-sm border-2 border-[#F9F6F0] text-[#F9F6F0] bg-transparent hover:bg-[#F9F6F0] hover:text-[#1A2F25] transition-all duration-300" data-testid="cta-pricing">See plans</Link>
            </div>
          </div>
          <div className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full bg-[#D47C59]/15 blur-3xl pointer-events-none" />
        </div>
      </section>

      {/* STICKY MOBILE CTA — signup always one tap away */}
      {showSticky && (
        <div className="sm:hidden fixed bottom-0 inset-x-0 z-30 bg-[#F9F6F0]/95 backdrop-blur-md border-t border-[#E5E0D8] px-4 py-3 pr-20 animate-fade-up" data-testid="sticky-mobile-cta">
          <Link
            to={designTo}
            onClick={() => { trackEvent("free_design_cta", { location: "sticky_mobile" }); trackEvent("design_cta_click", { location: "sticky_mobile" }); }}
            className="btn-accent w-full justify-center"
            data-testid="sticky-mobile-cta-button"
          >
            {designLabel} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
